import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'services/api';
import toastApi from 'services/toast';
import { getErrorMessage } from 'utils/errorHandler';
import { compareArrayOfObjects } from 'utils/arrayUtils';

import Button from 'components/button';
import TextAndInfo from 'components/textAndInfo';
import Badge from 'components/badge';
import Loader from 'components/loader';
import PreventExitModal from 'components/modal/preventExitModal';

import FiltersList from './FiltersList';

import {
  FilterTypeText,
  SaveButtonWrapper,
  DividerLine,
  ListStyled,
  StyledColForList,
  ModificationsText,
} from './styles';

const SearchFiltersTab = () => {
  const { t } = useTranslation();

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [hasModifications, setHasModifications] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [availableVehicleFilters, setAvailableVehicleFilters] = useState([]);
  const [availableProductFilters, setAvailableProductFilters] = useState([]);
  const originalSelectedFiltersRef = useRef([]);

  useEffect(() => {
    const onOpen = async () => {
      const filters = await api.loadSearchFilters();

      const selectedFiltersFromApi = filters.filter((filter) => filter.selected);
      const availableVehicleFiltersFromApi = filters.filter((filter) => !filter.selected && filter.fieldType === 'VEHICLE');
      const availableProductFiltersFromApi = filters.filter((filter) => !filter.selected && filter.fieldType === 'PRODUCT');

      originalSelectedFiltersRef.current = selectedFiltersFromApi;
      setSelectedFilters(selectedFiltersFromApi);
      setAvailableVehicleFilters(availableVehicleFiltersFromApi);
      setAvailableProductFilters(availableProductFiltersFromApi);
      setLoadingPage(false);
    };

    onOpen();
  }, []);

  useEffect(() => {
    const areEquals = compareArrayOfObjects(selectedFilters, originalSelectedFiltersRef.current);
    setHasModifications(!areEquals);
  }, [selectedFilters]);

  const selectAvailableFilter = (filter) => {
    const newItem = { ...filter, selected: true };
    setAvailableVehicleFilters(
      availableVehicleFilters.filter((value) => value.field !== newItem.field),
    );
    setAvailableProductFilters(
      availableProductFilters.filter((value) => value.field !== newItem.field),
    );
    setSelectedFilters([...selectedFilters, newItem]);
  };

  const removeSelectedFilter = (filter) => {
    if (selectedFilters.length === 1) {
      toastApi.warning(t('pages.searchVehiclePage.searchFiltersTab.atLeastOneSelectedFilter'));
      return;
    }
    const deletedItem = { ...filter, selected: false };

    if (deletedItem.fieldType === 'VEHICLE') {
      setAvailableVehicleFilters([...availableVehicleFilters, deletedItem]);
    } else {
      setAvailableProductFilters([...availableProductFilters, deletedItem]);
    }

    setSelectedFilters(
      selectedFilters.filter((value) => value.field !== deletedItem.field),
    );
  };

  const save = async () => {
    const sortedFilters = selectedFilters.map(
      (filter, index) => ({ ...filter, order: index + 1 }),
    );

    await api.saveSearchFilters(sortedFilters);
    toastApi.success(t('pages.searchVehiclePage.searchFiltersTab.successSaveMessage'));

    return sortedFilters;
  };

  const handleClickOnSave = async () => {
    try {
      setLoadingSave(true);

      const sortedFilters = await save();
      originalSelectedFiltersRef.current = sortedFilters;
      setSelectedFilters(sortedFilters);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastApi.error(errorMessage || error);
    } finally {
      setLoadingSave(false);
    }
  };

  const renderListContent = (item) => (
    <StyledColForList col={10}>
      <p>{item.name}</p>
      {!item.disableRequired && <Badge text={t('pages.searchVehiclePage.searchFiltersTab.badgeCanBeRequired')} variant="secondary" />}
    </StyledColForList>
  );

  const renderContent = () => (
    <>
      <PreventExitModal cantExit={hasModifications} onClickPrimaryButton={save} />
      <FiltersList
        filters={selectedFilters}
        setFilters={setSelectedFilters}
        onSelect={removeSelectedFilter}
      />

      <DividerLine />

      <TextAndInfo
        text={t('pages.searchVehiclePage.searchFiltersTab.otherFiltersText')}
        info={t('pages.searchVehiclePage.searchFiltersTab.otherFiltersText2')}
      />

      <FilterTypeText>{t('pages.searchVehiclePage.aboutVehicle')}</FilterTypeText>

      <ListStyled
        items={availableVehicleFilters}
        renderContent={renderListContent}
        selectLabel={t('common.select')}
        onSelect={selectAvailableFilter}
      />

      <FilterTypeText>{t('pages.searchVehiclePage.aboutProduct')}</FilterTypeText>

      <ListStyled
        items={availableProductFilters}
        renderContent={renderListContent}
        selectLabel={t('common.select')}
        onSelect={selectAvailableFilter}
      />

      <SaveButtonWrapper>
        <ModificationsText>{t(hasModifications ? 'common.thereAreUnsavedChanges' : 'common.thereAreNoChangesToSave')}</ModificationsText>
        <Button
          onClick={handleClickOnSave}
          loading={loadingSave}
          loadingLabel={t('common.saving')}
          disabled={!hasModifications}
        >
          {t('common.save')}
        </Button>
      </SaveButtonWrapper>
    </>
  );

  if (loadingPage) {
    return <Loader withWrapper />;
  }

  return renderContent();
};

export default SearchFiltersTab;
